import * as constants from '../../constants/MassScheduling';

// Helper function to round time to next 15-minute interval (matches backend logic)
const roundToNext15Minutes = (date) => {
  const ms = 1000 * 60 * 15; // 15 minutes in milliseconds
  return new Date(Math.ceil(date.getTime() / ms) * ms);
};

// Helper function to format timestamps in a user-friendly way
const formatTimestampForDisplay = (timestamp) => {
  const date = new Date(timestamp);
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  return date.toLocaleString('en-US', options);
};

export const validateline = (item, props) => {
  // returnValue can take values of 0, 1, -1
  // 1 => no errors found in the line yet
  // -1 => there is an error in this line
  // initialize returnValue = 1, which means everything is fine
  let returnValue = 1;
  const errors = [];

  // ensure location specified in this line is one of all available locations
  if (!Object.keys(props.locations).includes(item.location)) {
    errors.push(`Location not found in list of locations: ${item.location}`);
    returnValue = -1;
  }

  // validate csv header
  let item_keys = Object.keys(item);
  //remove _validation from item_keys
  item_keys = item_keys.filter((key) => key !== '_validation');
  // ensure location is present
  if (!item_keys.includes('location')) {
    errors.push('Location not present in header');
    returnValue = -1;
  }

  // if we are bulk scheduling holiday screen updates, both start and end dates should be present
  if (
    item_keys.includes('Holiday Screen Start Date') !==
    item_keys.includes('Holiday Screen End Date')
  ) {
    errors.push('Need both holiday start and end dates to schedule');
    returnValue = -1;
  }
  // validate timestamp format if holiday start and end dates are present
  // some locations in the CSV can have holiday screen dates specified and others may not
  // so we have to allow for empty values in both these fields
  if (
    item_keys.includes('Holiday Screen Start Date') &&
    item['Holiday Screen Start Date'] !== '' &&
    item_keys.includes('Holiday Screen End Date') &&
    item['Holiday Screen End Date'] !== ''
  ) {
    if (
      !constants.HOLIDAY_DATE_FORMAT.test(item['Holiday Screen Start Date']) ||
      !constants.HOLIDAY_DATE_FORMAT.test(item['Holiday Screen End Date'])
    ) {
      errors.push(
        `Holiday start / end date not in the required format YYYY-mm-dd for: ${item.location}`,
      );
      returnValue = -1;
    }
  }
  // if we are bulk scheduling Ready for Allocation updates, both allocation value and reason for change should be present
  if (
    item_keys.includes('Ready for Allocation') !==
    item_keys.includes('Ready for Allocation Update Reason')
  ) {
    errors.push(
      'Both allocation value and reason for update should be present in csv',
    );
    returnValue = -1;
  }
  // if ready for allocation is not empty, allocation reason should have a value
  if (
    item_keys.includes('Ready for Allocation') &&
    item['Ready for Allocation'] !== ''
  ) {
    if (!item_keys.includes('Ready for Allocation Update Reason')) {
      errors.push(
        'Both allocation value and reason for update should be present in csv',
      );
      returnValue = -1;
    } else if (item['Ready for Allocation Update Reason'] === '') {
      errors.push('Allocation update reason cannot be empty');
      returnValue = -1;
    }
  }
  // if fridge schema template is not empty, fridge schema template timestamp must have a value
  if (
    item_keys.includes('Fridge Schema Template') !==
    item_keys.includes('Fridge Schema Template Timestamp')
  ) {
    errors.push(
      'Both fridge schema template value and timestamp for update should be present in csv',
    );
    returnValue = -1;
  }
  // ensure all the keys in csv header are allowed i.e., in constants.CSV_KEYS[]
  for (let i = 0; i < item_keys.length; i++) {
    if (!constants.CSV_KEYS.includes(item_keys[i])) {
      errors.push(`${item_keys[i]} not allowed in the header`);
      returnValue = -1;
    }
    // ensure required attributes are present to schedule updates accurately
    if (constants.ATTR_WITH_TS.includes(item_keys[i])) {
      if (!item_keys.includes(`${item_keys[i]} Timestamp`)) {
        errors.push(`${item_keys[i]} does not have a schedule time specified`);
        returnValue = -1;
      } else {
        // schedule present as expected. Validate attribute value and schedule timestamp format
        // attribute values can only be one of the values specified in constants.ALLOWED_ATTRIBUTE_VALUES[attribute]
        const attribute = item_keys[i];
        const value = item[item_keys[i]];
        const timestamp = item[`${item_keys[i]} Timestamp`].toString(); // just to make sure timestamp is read as a string
        if (
          Object.keys(constants.ALLOWED_ATTRIBUTE_VALUES).includes(attribute) &&
          !constants.ALLOWED_ATTRIBUTE_VALUES[attribute].includes(value)
        ) {
          errors.push(`Value ${value} not allowed for ${attribute}`);
          returnValue = -1;
        }
        if (
          value !== '' &&
          !constants.SCHEDULE_TIMESTAMP_FORMAT.test(timestamp)
        ) {
          errors.push(
            `Timestamp for ${attribute} not in required format of YYYY-mm-dd HH:MM:SS`,
          );
          returnValue = -1;
        }
        // Validate that timestamp is not in the past
        // Note: Backend rounds timestamps to next 15-minute interval, so we validate against the rounded time
        if (
          value !== '' &&
          constants.SCHEDULE_TIMESTAMP_FORMAT.test(timestamp)
        ) {
          const scheduledTime = new Date(timestamp);
          const roundedTime = roundToNext15Minutes(scheduledTime);
          const now = new Date();

          if (roundedTime <= now) {
            const originalFormatted = formatTimestampForDisplay(timestamp);
            const roundedFormatted = formatTimestampForDisplay(roundedTime);

            // Show both times if they differ, otherwise just show the rounded time
            const timeMessage =
              originalFormatted !== roundedFormatted
                ? `${originalFormatted} (rounds to ${roundedFormatted})`
                : roundedFormatted;

            errors.push(
              `Timestamp for ${attribute} cannot be in the past. Scheduled time: ${timeMessage}`,
            );
            returnValue = -1;
          }
        }
      }
    }
  }
  for (let i = 0; i < constants.CSV_KEYS.length; i++) {
    const key = constants.CSV_KEYS[i];
    if (
      props.locations[item.location] &&
      props.locations[item.location][key] !== item[key]
    ) {
      if (returnValue === 1) {
        // change valid = 0 only if entry is not already invalid
        returnValue = 0;
      }
    }
  }
  return {
    status: returnValue,
    errors: errors,
  };
};

export const validateFile = (fileContents) => {
  if (fileContents === '') {
    return true;
  }
  return false;
};
